import * as THREE from "three";

/**
 * Replaces any non-Physical materials on an FBX object with MeshPhysicalMaterial.
 * FBXLoader cannot parse 3dsMax-specific parameter names (base_color_map, bump_map,
 * normalCamera) and silently skips those textures. It also defaults unknown material
 * types to MeshPhongMaterial. This function upgrades every material to
 * MeshPhysicalMaterial and migrates all recognised map/property values so nothing
 * is lost in translation.
 */
export function fixFBXMaterials(fbx: any) {
  fbx.traverse((child: any) => {
    if (!(child instanceof THREE.Mesh)) return;

    const fixMaterial = (mat: any): THREE.MeshPhysicalMaterial => {
      if (mat instanceof THREE.MeshPhysicalMaterial) {
        mat.dithering = true;
        return mat;
      }

      const physMat = new THREE.MeshPhysicalMaterial();

      // ── basic maps shared by Phong / Lambert / Physical ────────────────
      if (mat.map) physMat.map = mat.map;
      if (mat.normalMap) physMat.normalMap = mat.normalMap;
      if (mat.bumpMap) physMat.bumpMap = mat.bumpMap;
      if (mat.bumpScale != null) physMat.bumpScale = mat.bumpScale;
      if (mat.aoMap) physMat.aoMap = mat.aoMap;
      if (mat.aoMapIntensity != null) physMat.aoMapIntensity = mat.aoMapIntensity;
      if (mat.emissiveMap) physMat.emissiveMap = mat.emissiveMap;
      if (mat.alphaMap) physMat.alphaMap = mat.alphaMap;
      if (mat.envMap) physMat.envMap = mat.envMap;
      if (mat.lightMap) physMat.lightMap = mat.lightMap;

      // ── PBR maps present on MeshStandardMaterial / Physical ────────────
      if (mat.metalnessMap) physMat.metalnessMap = mat.metalnessMap;
      if (mat.roughnessMap) physMat.roughnessMap = mat.roughnessMap;

      // ── 3dsMax-specific maps skipped by FBXLoader ───────────────────────
      // base_color_map → diffuse/albedo colour map
      if (mat.userData?.base_color_map) physMat.map = mat.userData.base_color_map;
      // bump_map → bump (height) map
      if (mat.userData?.bump_map) physMat.bumpMap = mat.userData.bump_map;
      // normalCamera → normal map
      if (mat.userData?.normalCamera) physMat.normalMap = mat.userData.normalCamera;

      // ── scalar / colour properties ──────────────────────────────────────
      if (mat.color) physMat.color.copy(mat.color);
      if (mat.emissive) physMat.emissive.copy(mat.emissive);
      if (mat.opacity != null) physMat.opacity = mat.opacity;
      if (mat.transparent != null) physMat.transparent = mat.transparent;
      if (mat.alphaTest != null) physMat.alphaTest = mat.alphaTest;
      if (mat.side != null) physMat.side = mat.side;
      if (mat.depthTest != null) physMat.depthTest = mat.depthTest;
      if (mat.depthWrite != null) physMat.depthWrite = mat.depthWrite;

      // Convert shininess (Phong) to a roughness approximation
      if (mat.shininess != null) {
        physMat.roughness = 1.0 - Math.sqrt(Math.min(mat.shininess, 1000) / 1000);
      } else {
        physMat.roughness = 0.8;
      }

      // Specular intensity on Phong materials maps loosely to metalness
      if (mat.specular) {
        const specIntensity = (mat.specular.r + mat.specular.g + mat.specular.b) / 3;
        physMat.metalness = Math.min(specIntensity, 1.0);
      } else {
        physMat.metalness = 0.0;
      }

      physMat.dithering = true;
      physMat.name = mat.name;

      // Keep a reference to the source material for downstream debugging
      physMat.userData = { ...mat.userData, replacedMaterial: mat.type };

      mat.dispose();
      return physMat;
    };

    if (Array.isArray(child.material)) {
      child.material = child.material.map(fixMaterial);
    } else {
      child.material = fixMaterial(child.material);
    }
  });
}
